/**
 * ForcePasswordChangeScreen — bloquea el acceso a la app hasta que el usuario
 * elija su propia contraseña.
 *
 * POR QUÉ EXISTE:
 * Cuando un admin crea un usuario o resetea una contraseña, el admin conoce
 * esa clave temporal. Este componente obliga a cambiarla en el primer login
 * (o justo después de un reset) usando supabase.auth.updateUser(), que corre
 * con la sesión del propio usuario — el admin nunca ve la clave definitiva.
 *
 * Se renderiza dentro de ProtectedRoute cuando profile.must_change_password
 * es true, en vez de <Outlet/>. Bloquea toda navegación hasta completarse.
 */
import { useState } from 'react'
import { Eye, EyeOff, Loader2, AlertCircle, KeyRound, LogOut } from 'lucide-react'
import { supabase }        from '@/lib/supabase'
import { useAuthStore }    from '@/stores/auth.store'
import { LOGO_PROCREDITO } from '@/assets/logo'
import { stopInactivityMonitor } from '@/lib/inactivity'

const MIN_LENGTH = 8

export function ForcePasswordChangeScreen() {
  const profile = useAuthStore(s => s.profile)

  const [pwd,      setPwd]      = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [show,     setShow]     = useState(false)
  const [error,    setError]    = useState<string | null>(null)
  const [submitting, setSub]    = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (pwd.length < MIN_LENGTH) {
      setError(`La contraseña debe tener al menos ${MIN_LENGTH} caracteres.`)
      return
    }
    if (pwd !== confirm) {
      setError('Las contraseñas no coinciden.')
      return
    }

    setSub(true)
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password: pwd })
      if (updateErr) throw new Error(updateErr.message)

      const userId = useAuthStore.getState().user?.id
      if (!userId) throw new Error('Sesión inválida — vuelve a iniciar sesión.')

      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ must_change_password: false })
        .eq('id', userId)
      if (profileErr) throw new Error(profileErr.message)

      // Reflejar el cambio de inmediato en el store para que ProtectedRoute
      // deje de bloquear y renderice la app normal.
      if (profile) {
        useAuthStore.getState().setProfile({ ...profile, must_change_password: false })
      }
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo actualizar la contraseña.')
    } finally {
      setSub(false)
    }
  }

  const handleLogout = async () => {
    stopInactivityMonitor()
    await supabase.auth.signOut()
    useAuthStore.getState().reset()
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0a1628 50%, #0d1f3c 100%)' }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/[0.10] p-8"
        style={{ background: 'rgba(8,16,32,0.75)', backdropFilter: 'blur(20px)' }}>

        <div className="flex flex-col items-center mb-6">
          <img src={LOGO_PROCREDITO} alt="ProCredito" className="h-12 w-12 object-contain mb-3" />
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-500/10 border border-blue-500/20 mb-3">
            <KeyRound size={16} className="text-blue-400" />
          </div>
          <h2 className="text-lg font-bold text-white text-center">Elige tu contraseña</h2>
          <p className="text-blue-200/50 text-xs text-center mt-1">
            Por seguridad, debes definir tu propia contraseña antes de continuar.
            Nadie más la conocerá.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {error && (
            <div className="flex items-start gap-2.5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3">
              <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-xs text-red-400 leading-relaxed">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-blue-200/50 mb-1.5">
              Nueva contraseña (mín. {MIN_LENGTH} caracteres)
            </label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={pwd}
                onChange={e => setPwd(e.target.value)}
                autoFocus
                className="w-full rounded-lg px-3 py-2.5 pr-9 text-sm bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500"
                placeholder="Nueva contraseña"
              />
              <button type="button" onClick={() => setShow(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70">
                {show ? <EyeOff size={14}/> : <Eye size={14}/>}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-blue-200/50 mb-1.5">
              Confirmar contraseña
            </label>
            <input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="w-full rounded-lg px-3 py-2.5 text-sm bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500"
              placeholder="Repite la contraseña"
            />
          </div>

          <button type="submit" disabled={submitting || !pwd || !confirm}
            className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-500 disabled:opacity-40 transition-colors mt-1">
            {submitting && <Loader2 size={13} className="animate-spin"/>}
            Guardar y continuar
          </button>

          <button type="button" onClick={handleLogout}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-blue-200/40 hover:text-blue-200/70 transition-colors">
            <LogOut size={12}/> Cerrar sesión
          </button>
        </form>
      </div>
    </div>
  )
}
